import React, { FC, useState, useCallback, memo } from 'react';
import styled from 'styled-components';
import _isEmpty from 'lodash/isEmpty';
import _map from 'lodash/map';
import { gqlMediaTypeCodeEnum as MEDIA_TYPE } from '@minecraft.graphql-types';
import { useTranslation } from '@blocs.i18n';
import CloudinaryMediaViewer from '@minecraft.cloudinary-media-viewer';
import Icon from '@minecraft.icon';
import { CoreTheme as theme } from '@minecraft.themes';
import { SectionTitleWithIcon, SectionTitleIcon } from '../SummaryStyles';

const StyledPlayIcon = styled(Icon)`
  cursor: pointer;
  height: 1.125rem;
  width: 1.125rem;
  margin-left: 0.625rem;
`;

const CreditList = styled.div`
  margin-top: 0.625rem;
`;

const CreditItem = styled.div`
  display: flex;
  align-items: center;

  &:not(:first-child) {
    margin-top: 0.375rem;
  }
`;

type Props = {
  creditList: ReadonlyArray<any>;
};

const SummaryCredits: FC<Props> = ({ creditList }) => {
  const { t } = useTranslation();

  const [isMediaViewerOpen, setMediaViewerOpen] = useState(false);
  const [creditMedia, setCreditMedia] = useState(null);

  const handleMediaViewerToggle = useCallback(() => {
    setMediaViewerOpen(!isMediaViewerOpen);
  }, [isMediaViewerOpen]);

  const handleMediaViewerOpen = useCallback(
    (media) => {
      setCreditMedia({ media });
      handleMediaViewerToggle();
    },
    [handleMediaViewerToggle]
  );

  return (
    !_isEmpty(creditList) && (
      <div>
        <SectionTitleWithIcon>
          <SectionTitleIcon name="star-filled" color={theme.colors.primary.darkGrey} />
          {t('common:profile.info.highlightedCredits')}
        </SectionTitleWithIcon>
        <CreditList>
          {_map(creditList, (credit) => (
            <CreditItem key={credit.id}>
              {credit.title}
              {!_isEmpty(credit.creditMedia) && credit.creditMedia[0]?.mediaType?.code === MEDIA_TYPE.VIDEO && (
                <StyledPlayIcon
                  name="media-play-circle"
                  color={theme.colors.primary.primary}
                  onClick={(): void => handleMediaViewerOpen(credit.creditMedia[0])}
                />
              )}
            </CreditItem>
          ))}
        </CreditList>
        {isMediaViewerOpen && (
          <CloudinaryMediaViewer
            isSmall
            stageName=""
            mediaList={[creditMedia]}
            handleCloseModal={handleMediaViewerToggle}
          />
        )}
      </div>
    )
  );
};

export default memo<Props>(SummaryCredits);
